import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Input } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { MsgAction } from '../../store/actions';
import CustomModal from '../../components/Modal/CustomModal';
import NewConversationModal from '../../components/ChatLayout/NewConversationModal';
import { ModalHeader, ModalFooter, ModalButton } from '../../components/Modal/ModalParts';
import { Skeleton } from '../../components';
import { Utils } from '../../config';
import {
  CHANNEL_KIND,
  CONVERSATION_FILTER,
  ROUTES,
  SEARCH_DEBOUNCE_MS,
} from '../../config/constants';
import { useInfiniteList } from '../../hooks/useInfiniteList';
import { usePresence, PRESENCE, presenceLabel } from '../../hooks/usePresence';
import { formatListTime } from '../../config/util';
import './Messages.css';
import { CiSearch } from 'react-icons/ci';
import { MdOutlineDelete } from 'react-icons/md';
import SocketAPI from '../../config/socket/socket';
import {
  ConversationRow,
  SidebarPanel,
  SidebarHeader,
  ListBody,
  TabPill,
  TabRow,
  TabCount,
  EmptyState,
  titleCase,
  messagePreview,
} from '../../components/ChatLayout/ListRow';

const DELETE_TARGET = { CHAT: 'chat', GROUP: 'group' };

const MessagesSideBar = ({ openPane }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { memberName } = useParams();

  const userData = useSelector((state) => state?.AppReducer?.user?.data);
  const { chat_list, listing_loader: loader } = useSelector((state) => state?.MsgReducer);
  const nextCursor = useSelector((state) => state?.MsgReducer?.chat_list_next_cursor);
  const ChatList = chat_list?.data?.chats?.rows;
  const GroupList = chat_list?.data?.groups?.rows;

  const [modal, setModal] = useState(false);
  const [filter, setFilter] = useState(CONVERSATION_FILTER.ALL);
  const [searchGlobal, setSearchGlobal] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, type: DELETE_TARGET }

  const socket = SocketAPI();

  /**
   * The 1:1 counterparts currently rendered. Presence is a subscription now, not a
   * firehose — see `usePresence`, which also owns the shared store this reads back from,
   * so this row's dot and the open conversation's header cannot disagree.
   */
  const visibleCounterpartIds = useMemo(
    () => (ChatList ?? []).map((row) => row?.chat_member?.user?.id).filter(Boolean),
    [ChatList]
  );
  const { statusOf } = usePresence(socket, visibleCounterpartIds);

  useEffect(() => {
    if (!socket) return undefined;

    // Named handler, removed by reference. The connection is shared app-wide, so a bare
    // `off('message:alert')` would also strip the group sidebar's listener for the same
    // event.
    // Patch the one row this concerns. This used to re-issue the whole conversation
    // list — an unbounded read of every channel the reader belongs to, on every inbound
    // message from anywhere. Now that the list is paged it would also be destructive:
    // a refetch returns page one, discarding every page the reader has scrolled through.
    //
    // A message for a conversation NOT in the loaded list has nothing to patch, so that
    // case — and only that case — reloads the first page.
    const onMessageAlert = (alert) => {
      const channelId = alert?.channel_id;
      if (!channelId) return;

      const known = [...(chatRowsRef.current || []), ...(groupRowsRef.current || [])].some(
        (row) => String(row?.id) === String(channelId)
      );

      if (!known) {
        dispatch(MsgAction.GlobalSearchChatsGroups({ search: searchRef.current }));
        return;
      }

      dispatch(
        MsgAction.ConversationTouched({
          channel_id: channelId,
          message: alert?.message,
          sender_id: alert?.sender_id,
          // `memberName` is the id in the URL — the conversation currently open.
          is_open: String(openConversationRef.current) === String(channelId),
        })
      );
    };
    socket.on('message:alert', onMessageAlert);

    // The missed-call seed that used to sit here is gone, and `userData?.id` went with
    // it from the dependencies — it was the seed's only reader, and leaving it in
    // re-subscribed this listener the first time the profile resolved. App.js owns the
    // badge now, and re-seeds it on reconnect; two copies racing to write one counter
    // was the only thing this sidebar contributed.
    return () => {
      socket.off('message:alert', onMessageAlert);
    };
  }, [dispatch, socket]);

  // Read from refs inside the socket handler so it does not have to re-subscribe on
  // every list change — re-subscribing on each inbound message is how listeners
  // multiply on a shared connection.
  const chatRowsRef = useRef(ChatList);
  const groupRowsRef = useRef(GroupList);
  const searchRef = useRef('');
  const openConversationRef = useRef(memberName);
  chatRowsRef.current = ChatList;
  groupRowsRef.current = GroupList;
  openConversationRef.current = memberName;

  // The term the list was last FETCHED with. Search runs on the server — only one page
  // is in hand, so filtering it locally would search a fraction of the conversations and
  // present the result as all of them.
  const [term, setTerm] = useState('');
  searchRef.current = term;

  const loadedCount = (ChatList?.length ?? 0) + (GroupList?.length ?? 0);

  const fetchPage = useCallback(
    (cursor) => dispatch(MsgAction.GlobalSearchChatsGroups({ search: term, cursor })),
    [dispatch, term]
  );

  // `resetKey` folds in the search term, so changing it rewinds paging and re-reads from
  // the first page rather than appending results for one term onto another's.
  const { sentinelRef, exhausted } = useInfiniteList({
    resetKey: `conversations:${term}`,
    fetchPage,
    loading: loader,
    loadedCount,
    nextCursor,
  });

  // Functions

  const ModalCancel = () => setModal(false);

  // Selecting only records the choice and routes — ConversationPane fetches the
  // channel, so a deep link or a refresh loads the same way a click does.
  const openConversation = (conversation, routeId) => {
    openPane();
    dispatch(MsgAction.SelectedChatUser(conversation));
    navigate(`${ROUTES.MESSAGES}/${routeId}`);
  };

  const handleSelectedChat = (selectedChat) =>
    openConversation(selectedChat, selectedChat?.chat_member?.user?.id);

  const handleSelectedGroup = (selectedGroup) => openConversation(selectedGroup, selectedGroup?.id);

  /**
   * The timer has to outlive the handler to be cancellable. It was a local
   * `let timeout` before, so `clearTimeout` always cleared `undefined` and
   * every keystroke fired its own search request — the debounce was inert.
   */
  const searchTimer = useRef(null);

  useEffect(() => () => clearTimeout(searchTimer.current), []);

  const globalSearchHandler = (event) => {
    const query = event?.target?.value ?? '';
    setSearchGlobal(query);
    clearTimeout(searchTimer.current);
    // Sets the TERM rather than dispatching. The paging hook owns the request: it
    // rewinds to the first page when the term changes, which a bare dispatch would not
    // do — the cursor would still be pointing into the previous term's result set.
    searchTimer.current = setTimeout(() => setTerm(query.trim()), SEARCH_DEBOUNCE_MS);
  };

  /**
   * The subscription's answer, with the row's own `user_online_status` as the fallback
   * for a page that has just landed and not been subscribed yet.
   *
   * The client used to derive this itself from a raw `last_seen`: ONLINE under five
   * minutes, AWAY under thirty. Both numbers were wrong for the same reason —
   * `last_seen` is written on DISCONNECT, so "under five minutes" is precisely the
   * window in which someone has just left. It also ignored the owner's Last Seen
   * setting, which the server honours by withholding the timestamp; with the timestamp
   * gone the derivation quietly returned OFFLINE for the wrong reason, and the socket
   * list it consulted first was never gated at all, so a user who had hidden their
   * presence still showed a green dot.
   */
  const getUserStatus = useCallback(
    (user) => statusOf(user?.id) ?? user?.user_online_status ?? PRESENCE.OFFLINE,
    [statusOf]
  );

  const requestDelete = (id, type) => {
    setDeleteTarget({ id, type });
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;

    dispatch(
      MsgAction.DeleteChannel(deleteTarget.id, () => {
        dispatch(MsgAction.GlobalSearchChatsGroups({ search: term }));
        Utils.showMessage(
          'success',
          deleteTarget.type === DELETE_TARGET.CHAT
            ? 'Chat deleted successfully'
            : 'Group deleted successfully'
        );

        navigate(ROUTES.MESSAGES, { replace: true });
      })
    );
    setDeleteConfirmOpen(false);
    setDeleteTarget(null);
  };

  const cancelDelete = () => {
    setDeleteConfirmOpen(false);
    setDeleteTarget(null);
  };

  // One merged, recency-sorted list. Sort on a copy — the reducer's arrays
  // must not be mutated in render. Memoised because merging and sorting both
  // lists on every keystroke and every hover was the bulk of a render.
  const conversations = useMemo(() => {
    const byRecency = (a, b) => new Date(b?.updatedAt) - new Date(a?.updatedAt);
    return [
      ...(ChatList || []).map((item) => ({ item, isGroup: false })),
      ...(GroupList || []).map((item) => ({ item, isGroup: true })),
    ].sort((a, b) => byRecency(a.item, b.item));
  }, [ChatList, GroupList]);

  const unreadOf = (entry) => Number(entry.item?.chat_member?.unread_message_count) || 0;

  // A channel is a group only its admins can post in. The server labels it with
  // `channel_kind`; `is_broadcast` is the legacy shape of the same fact, accepted so
  // the tab keeps working against an API that has not been redeployed yet.
  const isChannel = (entry) =>
    entry.isGroup &&
    (entry.item?.channel_kind === CHANNEL_KIND.CHANNEL || Boolean(entry.item?.is_broadcast));

  const { visible, unreadTotal, groupTotal, channelTotal } = useMemo(() => {
    let unread = 0;
    let groups = 0;
    let channels = 0;
    for (const entry of conversations) {
      if (unreadOf(entry) > 0) unread += 1;
      if (isChannel(entry)) channels += 1;
      // A channel is not also listed under Groups — the two tabs partition the
      // group-shaped conversations rather than nesting.
      else if (entry.isGroup) groups += 1;
    }

    const matches = conversations.filter((entry) => {
      if (filter === CONVERSATION_FILTER.UNREAD) return unreadOf(entry) > 0;
      if (filter === CONVERSATION_FILTER.CHANNELS) return isChannel(entry);
      if (filter === CONVERSATION_FILTER.GROUPS) return entry.isGroup && !isChannel(entry);
      return true;
    });

    return {
      visible: matches,
      unreadTotal: unread,
      groupTotal: groups,
      channelTotal: channels,
    };
  }, [conversations, filter]);

  const totalCount = conversations.length;

  const FILTERS = [
    { key: CONVERSATION_FILTER.ALL, label: 'All' },
    { key: CONVERSATION_FILTER.UNREAD, label: 'Unread', count: unreadTotal },
    { key: CONVERSATION_FILTER.GROUPS, label: 'Groups', count: groupTotal },
    { key: CONVERSATION_FILTER.CHANNELS, label: 'Channels', count: channelTotal },
  ];

  return (
    <SidebarPanel>
      {/* --- Header: title, compose, search, filters ------------------------ */}
      <SidebarHeader
        title="Messages"
        action={
          <button
            type="button"
            className="sidebar-compose"
            title="New chat or group"
            onClick={() => setModal(true)}
          >
            <span aria-hidden="true">+</span>
            New
          </button>
        }
      >
        <Input
          onChange={globalSearchHandler}
          size="medium"
          placeholder="Search or start a new chat"
          prefix={<CiSearch />}
        />

        <TabRow flush>
          {FILTERS.map(({ key, label, count }) => (
            <TabPill key={key} active={filter === key} onClick={() => setFilter(key)}>
              {label}
              {count > 0 && <TabCount>{count}</TabCount>}
            </TabPill>
          ))}
        </TabRow>
      </SidebarHeader>

      {/* --- Conversation list ---------------------------------------------- */}
      <ListBody>
        {loader ? (
          // Rows rather than a spinner: the list's shape is known before its
          // contents are, so showing it costs nothing and the pane stops looking
          // empty while it loads.
          <Skeleton.List rows={7} />
        ) : (
          visible.map(({ item, isGroup }) => {
            if (isGroup) {
              return (
                <ConversationRow
                  key={`g-${item?.id}`}
                  name={titleCase(item?.name || 'Group')}
                  seed={item?.id}
                  avatarSrc={item?.media?.media_url}
                  preview={
                    messagePreview(item, {
                      isGroup: true,
                      meId: userData?.id,
                      isSecret: item?.is_secret,
                    }) ?? 'Group'
                  }
                  time={formatListTime(item?.updatedAt)}
                  unread={item?.chat_member?.unread_message_count}
                  active={String(memberName) === String(item?.id)}
                  onActivate={() => handleSelectedGroup(item)}
                  onDelete={() => requestDelete(item?.id, DELETE_TARGET.GROUP)}
                  DeleteIcon={MdOutlineDelete}
                />
              );
            }

            const user = item?.chat_member?.user;
            const status = getUserStatus(user);

            return (
              <ConversationRow
                key={`c-${item?.id}`}
                name={titleCase(user?.first_name || 'Unknown')}
                seed={user?.id ?? item?.id}
                avatarSrc={user?.media?.media_url}
                preview={messagePreview(item, { meId: userData?.id }) ?? presenceLabel(status)}
                time={formatListTime(item?.updatedAt)}
                unread={item?.chat_member?.unread_message_count}
                online={status === PRESENCE.ONLINE}
                active={String(memberName) === String(user?.id)}
                onActivate={() => handleSelectedChat(item)}
                onDelete={() => requestDelete(item?.id, DELETE_TARGET.CHAT)}
                DeleteIcon={MdOutlineDelete}
              />
            );
          })
        )}

        {!loader && visible.length === 0 && totalCount > 0 && (
          <EmptyState>
            {filter === CONVERSATION_FILTER.UNREAD ? 'Nothing unread.' : 'No groups yet.'}
          </EmptyState>
        )}

        {!loader && totalCount === 0 && (
          <EmptyState>
            {searchGlobal ? `Nothing matches “${searchGlobal}”.` : 'No conversations yet.'}
          </EmptyState>
        )}

        {/* Watched by an IntersectionObserver rather than an onScroll handler. It also
            fires on MOUNT, which is what loads a second page when the first is too
            short to fill a tall pane — the case where no scroll is possible, so no
            scroll event ever fires, and the list looks like it ends at one page. */}
        {!exhausted && totalCount > 0 && <div ref={sentinelRef} aria-hidden="true" />}
      </ListBody>

      {/* --- New chat / new group popup ------------------------------------- */}
      <NewConversationModal open={modal} onClose={ModalCancel} />

      {/* Delete Confirmation Modal */}
      <CustomModal
        open={deleteConfirmOpen}
        cancelModal={cancelDelete}
        centered={true}
        destroyOnClose={true}
        width={450}
        Children={
          <div style={{ width: '100%' }}>
            <ModalHeader
              title={deleteTarget?.type === DELETE_TARGET.CHAT ? 'Delete chat?' : 'Delete group?'}
              subtitle={
                deleteTarget?.type === DELETE_TARGET.CHAT
                  ? 'The conversation will be removed from your list. This cannot be undone.'
                  : 'The group will be removed from your list. This cannot be undone.'
              }
              onClose={cancelDelete}
            />
            <ModalFooter>
              <ModalButton variant="ghost" onClick={cancelDelete}>
                Cancel
              </ModalButton>
              <ModalButton variant="danger" onClick={confirmDelete}>
                Delete
              </ModalButton>
            </ModalFooter>
          </div>
        }
      />
    </SidebarPanel>
  );
};

export default MessagesSideBar;
